import { getCard, type GroupId } from '@lafayette/content';
import type { CommandBody, GameProjection, OpponentView } from '@lafayette/protocol';
import { Card, CardBack, MallStack } from '@lafayette/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { alertsForTransition, playAlertSound } from '../alerts.js';
import { useSettings } from '../settings.js';
import { useTurnAutomation } from '../useTurnAutomation.js';
import { Controls } from './Controls.js';
import { GameOver } from './GameOver.js';
import { SettingsMenu } from './SettingsMenu.js';
import { explainNotPayable, paymentPlan, togglePayment } from './payment.js';
import {
  ActionImpactPopup,
  CardTransferAnimations,
  TurnEndingPopup,
  type ImpactCue,
  type TransferCue,
} from './Presentation.js';
import {
  applyTarget,
  explainIneligible,
  isDraftReady,
  targetStepFor,
  type ActionDraft,
} from './targeting.js';
import styles from './GameTable.module.css';
import {
  everyone,
  legalOfType,
  nameOf,
  REJECTION_TEXT,
  stageHeadline,
  titleOf,
  waitingOn,
} from './selectors.js';

export interface GameTableProps {
  projection: GameProjection;
  send: (body: CommandBody) => void;
  rejection: string | null;
  connectionStatus: 'connecting' | 'connected' | 'reconnecting' | 'error';
}

export function GameTable({ projection, send, rejection, connectionStatus }: GameTableProps) {
  const [settings] = useSettings();
  const [selectedHandCard, setSelectedHandCard] = useState<string | null>(null);
  const [paymentSelection, setPaymentSelection] = useState<string[]>([]);
  const [discardSelection, setDiscardSelection] = useState<string[]>([]);
  const [zoomed, setZoomed] = useState<{ instanceId: string; group?: GroupId } | null>(null);
  const [draft, setDraft] = useState<ActionDraft | null>(null);
  const [celebrated, setCelebrated] = useState(false);
  const [transferCues, setTransferCues] = useState<TransferCue[]>([]);
  const [impactQueue, setImpactQueue] = useState<ImpactCue[]>([]);
  const seenPresentationVersion = useRef(projection.version);
  const previousAlertProjection = useRef<GameProjection | null>(null);
  const turnAutomation = useTurnAutomation(projection, send, connectionStatus === 'connected');

  // A card target is pointed at on the table rather than picked from a list.
  const targetStep = targetStepFor(projection, draft);

  // Paying works the same way: cards are staged straight off your own table.
  const payCommand = legalOfType(projection, 'payDebt')[0];
  const plan = payCommand ? paymentPlan(projection, payCommand, paymentSelection) : null;

  // A card that left your hand can no longer be the subject of a control panel.
  useEffect(() => {
    if (selectedHandCard && !projection.self.hand.includes(selectedHandCard)) {
      setSelectedHandCard(null);
    }
  }, [projection.self.hand, selectedHandCard]);

  useEffect(() => {
    if (!payCommand) setPaymentSelection([]);
    if (projection.stage !== 'discard') setDiscardSelection([]);
  }, [payCommand, projection.stage]);

  // Any server update invalidates a half-built action.
  useEffect(() => {
    setDraft(null);
    setZoomed(null);
  }, [projection.version]);

  // Do not replay old motion or alerts when a player first opens or refreshes
  // the table. Every later command is handled once, even if presence updates
  // rebroadcast the same projection version.
  useEffect(() => {
    if (projection.version <= seenPresentationVersion.current) return;
    seenPresentationVersion.current = projection.version;
    const transfers = projection.presentation.filter(
      (cue): cue is TransferCue => cue.type === 'cardsTransferred',
    );
    const impacts = projection.presentation.filter(
      (cue): cue is ImpactCue => cue.type === 'actionAffectedYou',
    );
    if (transfers.length > 0) setTransferCues((current) => [...current, ...transfers]);
    if (impacts.length > 0) {
      setImpactQueue((current) => {
        const known = new Set(current.map((cue) => cue.id));
        return [...current, ...impacts.filter((cue) => !known.has(cue.id))];
      });
    }
  }, [projection.presentation, projection.version]);

  useEffect(() => {
    const alerts = alertsForTransition(previousAlertProjection.current, projection);
    previousAlertProjection.current = projection;
    if (settings.alertSounds && alerts.length > 0) playAlertSound(settings.alertVolume);
  }, [projection, settings.alertSounds, settings.alertVolume]);

  // Losing sweeps your own cards off the table until you ask to see them again.
  const lost = projection.winnerId !== null && projection.winnerId !== projection.self.id;
  const tumbling = lost && !celebrated;

  // Lets the mall stacks drop their own clipping while cards fall away.
  useEffect(() => {
    const root = document.documentElement;
    if (tumbling) root.dataset.matchOver = 'lost';
    else delete root.dataset.matchOver;
    return () => {
      delete root.dataset.matchOver;
    };
  }, [tumbling]);

  const submitAction = useCallback(
    (ready: ActionDraft) => {
      send({
        type: 'playAction',
        cardInstanceId: ready.option.cardInstanceId,
        targets: ready.targets,
        doubleRentInstanceIds: ready.doubles,
      });
      setDraft(null);
      setZoomed(null);
      setSelectedHandCard(null);
    },
    [send],
  );

  const completeTransfer = useCallback((id: string) => {
    setTransferCues((current) => current.filter((cue) => cue.id !== id));
  }, []);

  /** Commits the card in the detail view to the step in progress. */
  const chooseTarget = useCallback(
    (instanceId: string) => {
      if (!draft || !targetStep) return;
      const targets = applyTarget(projection, targetStep, draft.targets, instanceId);
      const next = { ...draft, targets };
      setZoomed(null);
      if (isDraftReady(next.option, targets)) submitAction(next);
      else setDraft(next);
    },
    [draft, projection, submitAction, targetStep],
  );

  const discardCommand = legalOfType(projection, 'discardCards')[0];
  const isDiscarding = discardCommand !== undefined;

  const onHandCardClick = useCallback(
    (instanceId: string) => {
      if (isDiscarding) {
        setDiscardSelection((current) =>
          current.includes(instanceId)
            ? current.filter((id) => id !== instanceId)
            : [...current, instanceId],
        );
        return;
      }
      setSelectedHandCard((current) => (current === instanceId ? null : instanceId));
    },
    [isDiscarding],
  );

  /** Stages or unstages one of your own cards for the debt in progress. */
  const stageForPayment = useCallback(
    (instanceId: string) => {
      if (!plan) return;
      if (!plan.toggleableIds.includes(instanceId)) {
        setZoomed({ instanceId });
        return;
      }
      setPaymentSelection(togglePayment(plan, instanceId));
    },
    [plan],
  );

  const seats = everyone(projection);
  const opponents = seats.filter((s) => !s.isSelf);
  const headline = stageHeadline(projection);
  const waiting = waitingOn(projection);
  const actionsLeft = projection.turn.actionsPerTurn - projection.turn.actionsUsed;
  const requestedCardIds = requestedCardsFromSelf(projection);

  return (
    <div className={styles.shell}>
      <header className={styles.status} data-testid="status-bar">
        <div className={styles.statusMain}>
          <h1 className={styles.headline} data-testid="stage-headline">
            {headline}
          </h1>
          {waiting ? (
            <p className={styles.waiting} data-testid="waiting-on" role="status">
              {waiting}
            </p>
          ) : null}
        </div>
        <div className={styles.statusEnd}>
          <SettingsMenu />
          <dl className={styles.counters}>
            <div className={styles.counter}>
              <dt>Turn</dt>
              <dd data-testid="turn-player">{nameOf(projection, projection.turn.playerId)}</dd>
            </div>
            <div className={styles.counter}>
              <dt>Actions</dt>
              <dd data-testid="actions-left">
                {actionsLeft}/{projection.turn.actionsPerTurn}
              </dd>
            </div>
            <div className={styles.counter}>
              <dt>Draw</dt>
              <dd data-testid="draw-count">{projection.drawPileCount}</dd>
            </div>
          </dl>
        </div>
      </header>

      {rejection ? (
        <p className={styles.rejection} role="alert" data-testid="rejection">
          {REJECTION_TEXT[rejection] ?? 'That move was rejected.'}
        </p>
      ) : null}

      <section className={styles.opponents} aria-label="Opponents">
        {opponents.map((opponent) => (
          <OpponentPanel
            key={opponent.id}
            projection={projection}
            opponent={opponent}
            isTurn={projection.turn.playerId === opponent.id}
            targetableIds={targetStep?.legalIds ?? []}
            onZoom={(instanceId, group) => setZoomed({ instanceId, group })}
          />
        ))}
      </section>

      <section className={styles.middle} aria-label="Table centre">
        <div className={styles.piles}>
          <div className={styles.pile}>
            <CardBack size="sm" label={`Draw pile, ${projection.drawPileCount} cards`} />
            <span className={styles.pileLabel}>Draw &middot; {projection.drawPileCount}</span>
          </div>
          <div className={styles.pile}>
            {projection.discardTop ? (
              <Card
                definitionId={projection.cards[projection.discardTop]!}
                size="sm"
                context="top of the discard pile"
              />
            ) : (
              <div className={styles.emptyPile} role="img" aria-label="Empty discard pile" />
            )}
            <span className={styles.pileLabel}>Discard &middot; {projection.discardPileCount}</span>
          </div>
        </div>

        <ol className={styles.history} aria-label="Action history" data-testid="history">
          {projection.history
            .slice(-14)
            .reverse()
            .map((entry, index) => (
              <li key={`${entry.version}-${index}`}>{entry.text}</li>
            ))}
        </ol>
      </section>

      <section
        className={`${styles.self} ${tumbling ? styles.tumbling : ''}`}
        aria-label="Your board"
        data-player-zone={projection.self.id}
      >
        <PlayerBoard
          projection={projection}
          player={projection.self}
          isSelf
          targetableIds={plan ? plan.toggleableIds : (targetStep?.legalIds ?? [])}
          selectedIds={plan ? plan.selected : []}
          requestedIds={requestedCardIds}
          onPay={plan ? stageForPayment : undefined}
          onZoom={(instanceId, group) => setZoomed({ instanceId, group })}
        />
      </section>

      <div className={styles.bottom}>
        <Controls
          projection={projection}
          send={send}
          selectedHandCard={selectedHandCard}
          onSelectHandCard={setSelectedHandCard}
          onClearPayment={() => setPaymentSelection([])}
          discardSelection={discardSelection}
          onClearDiscard={() => setDiscardSelection([])}
          draft={draft}
          onDraftChange={setDraft}
          onConfirmAction={submitAction}
          plan={plan}
          drawProgress={turnAutomation.drawProgress}
          endTurnProgress={turnAutomation.endTurnProgress}
          onDrawForTurn={turnAutomation.drawForTurn}
          onEndTurn={turnAutomation.endTurn}
        />

        <section
          className={`${styles.hand} ${tumbling ? styles.tumbling : ''}`}
          aria-label="Your hand"
          data-testid="hand"
        >
          {projection.self.hand.map((instanceId) => (
            <Card
              key={instanceId}
              definitionId={projection.cards[instanceId]!}
              size="md"
              selected={selectedHandCard === instanceId || discardSelection.includes(instanceId)}
              context="in your hand"
              onClick={() => onHandCardClick(instanceId)}
            />
          ))}
          {projection.self.hand.length === 0 ? (
            <p className={styles.emptyHand}>Your hand is empty.</p>
          ) : null}
        </section>
      </div>

      <GameOver
        projection={projection}
        dismissed={celebrated}
        onDismiss={() => setCelebrated(true)}
      />

      <CardTransferAnimations cues={transferCues} onComplete={completeTransfer} />

      {impactQueue[0] ? (
        <ActionImpactPopup
          cue={impactQueue[0]}
          actorName={nameOf(projection, impactQueue[0].actorId)}
          onDismiss={() => setImpactQueue((current) => current.slice(1))}
        />
      ) : null}

      {turnAutomation.showEndTurnNotice ? (
        <TurnEndingPopup
          seconds={turnAutomation.endTurnSeconds}
          paused={turnAutomation.endTurnPaused}
          onWait={turnAutomation.pauseEndTurn}
        />
      ) : null}

      {zoomed ? (
        <div
          className={styles.zoomOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={titleOf(projection, zoomed.instanceId)}
          onClick={() => setZoomed(null)}
        >
          <div className={styles.zoomPanel} onClick={(e) => e.stopPropagation()}>
            <Card
              definitionId={projection.cards[zoomed.instanceId]!}
              size="lg"
              group={zoomed.group}
              tenantName={tenantNameOf(projection, zoomed.instanceId)}
            />
            {plan && !targetStep ? (
              plan.toggleableIds.includes(zoomed.instanceId) ? (
                <button
                  type="button"
                  className="lf-button lf-button--primary"
                  data-testid="confirm-target"
                  onClick={() => {
                    setPaymentSelection(togglePayment(plan, zoomed.instanceId));
                    setZoomed(null);
                  }}
                >
                  {plan.selected.includes(zoomed.instanceId) ? 'Take back' : 'Use to pay'}
                </button>
              ) : (
                <p className={styles.zoomReason} data-testid="target-reason">
                  {explainNotPayable(projection, plan, zoomed.instanceId)}
                </p>
              )
            ) : null}
            {targetStep ? (
              targetStep.legalIds.includes(zoomed.instanceId) ? (
                <button
                  type="button"
                  className="lf-button lf-button--primary"
                  data-testid="confirm-target"
                  onClick={() => chooseTarget(zoomed.instanceId)}
                >
                  {targetStep.confirmLabel}
                </button>
              ) : (
                <p className={styles.zoomReason} data-testid="target-reason">
                  {explainIneligible(
                    projection,
                    targetStep,
                    zoomed.instanceId,
                    projection.cards[zoomed.instanceId]!,
                  )}
                </p>
              )
            ) : null}
            <button type="button" className="lf-button" onClick={() => setZoomed(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The invented tenant a wildcard was played under, if it has one. */
function tenantNameOf(projection: GameProjection, instanceId: string): string | undefined {
  for (const player of everyone(projection)) {
    for (const mall of Object.values(player.malls)) {
      const name = mall?.tenantNames[instanceId];
      if (name) return name;
    }
  }
  return undefined;
}

/** Public cards on your table that the pending action is asking to take. */
function requestedCardsFromSelf(projection: GameProjection): string[] {
  const pending = projection.pendingAction;
  if (!pending?.affected.includes(projection.self.id)) return [];

  const property = pending.targets.propertyInstanceId;
  if (property) {
    const belongsToSelf = Object.values(projection.self.malls).some((mall) =>
      mall?.cards.includes(property),
    );
    return belongsToSelf ? [property] : [];
  }

  if (pending.targets.playerId !== projection.self.id || !pending.targets.group) return [];
  const mall = projection.self.malls[pending.targets.group];
  return mall ? [...mall.cards, ...mall.upgrades] : [];
}

function OpponentPanel({
  projection,
  opponent,
  isTurn,
  targetableIds,
  onZoom,
}: {
  projection: GameProjection;
  opponent: OpponentView;
  isTurn: boolean;
  targetableIds: string[];
  onZoom: (instanceId: string, group?: GroupId) => void;
}) {
  const owesYou = projection.debts.filter((d) => d.debtorId === opponent.id);
  return (
    <article
      className={`${styles.opponent} ${isTurn ? styles.opponentTurn : ''}`}
      data-testid={`opponent-${opponent.name}`}
      data-player-zone={opponent.id}
    >
      <header className={styles.opponentHead}>
        <span
          className={`${styles.dot} ${opponent.connected ? styles.dotOn : styles.dotOff}`}
          title={opponent.connected ? 'Connected' : 'Disconnected'}
        />
        <b>{opponent.name}</b>
        <span className={styles.meta}>
          {opponent.handCount} in hand &middot; ${opponent.bankTotal}M banked
        </span>
        {!opponent.connected ? <span className={styles.away}>Away</span> : null}
        {owesYou.length > 0 ? (
          <span className={styles.owes}>owes ${owesYou.reduce((s, d) => s + d.amount, 0)}M</span>
        ) : null}
      </header>
      <div
        className={styles.handRow}
        role="img"
        aria-label={`${opponent.name} holds ${opponent.handCount} cards`}
      >
        {Array.from({ length: Math.min(opponent.handCount, 7) }, (_, i) => (
          <CardBack key={i} size="xs" label="" />
        ))}
      </div>
      <PlayerBoard
        projection={projection}
        player={opponent}
        targetableIds={targetableIds}
        onZoom={onZoom}
      />
    </article>
  );
}

function PlayerBoard({
  projection,
  player,
  isSelf = false,
  targetableIds = [],
  selectedIds = [],
  requestedIds = [],
  onPay,
  onZoom,
}: {
  projection: GameProjection;
  player: OpponentView;
  isSelf?: boolean;
  targetableIds?: string[];
  selectedIds?: string[];
  requestedIds?: string[];
  /** Set while this player owes a debt; clicking a card stages it. */
  onPay?: (instanceId: string) => void;
  onZoom: (instanceId: string, group?: GroupId) => void;
}) {
  const malls = Object.entries(player.malls) as [GroupId, OpponentView['malls'][GroupId]][];
  return (
    <div className={styles.board}>
      <div className={styles.malls}>
        {malls.map(([group, mall]) =>
          mall ? (
            <MallStack
              key={group}
              group={group}
              cards={mall.cards}
              upgrades={mall.upgrades}
              tenantNames={mall.tenantNames}
              definitions={projection.cards}
              complete={player.completeMalls.includes(group)}
              ownerName={isSelf ? 'your' : player.name}
              size="md"
              selectableCardIds={mall.cards}
              targetableCardIds={targetableIds}
              selectedCardIds={selectedIds}
              requestedCardIds={requestedIds}
              onSelectUpgrade={onPay}
              onSelectCard={(instanceId) => (onPay ? onPay(instanceId) : onZoom(instanceId, group))}
            />
          ) : null,
        )}
        {malls.length === 0 ? <p className={styles.emptyBoard}>No properties yet.</p> : null}
      </div>

      <div className={styles.bank} data-testid={isSelf ? 'your-bank' : undefined}>
        <span className={styles.bankLabel}>Bank ${player.bankTotal}M</span>
        <div className={styles.bankCards}>
          {player.bank.map((instanceId) => {
            const definitionId = projection.cards[instanceId];
            if (!definitionId) return null;
            return (
              <Card
                key={instanceId}
                definitionId={definitionId}
                size="xs"
                context={`${isSelf ? 'your' : `${player.name}'s`} bank`}
                targetable={targetableIds.includes(instanceId)}
                selected={selectedIds.includes(instanceId)}
                onClick={() => (onPay ? onPay(instanceId) : onZoom(instanceId))}
              />
            );
          })}
          {player.bank.length === 0 ? <span className={styles.emptyBank}>empty</span> : null}
        </div>
      </div>
    </div>
  );
}

export { getCard };
